import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { useNetwork } from './NetworkContext'
import { useCurrentUser } from './UserContext'
import type { Network } from '@/services/networks/networksApi'
import type { ApiUser, NetworkPermissionFlags } from '@/services/users/userApi'
import { isSuperAdmin } from '@/data/superAdmins'
import { StorageService } from '@/services/core/storage'
import { useHasRetailProducts } from '@/hooks/useHasRetailProducts'

export interface ModuleNavItem {
  id: string
  icon: string
  label: string
  href?: string
  badge?: string
}

export interface ModuleNavSection {
  title?: string
  items: ModuleNavItem[]
}

export interface Module {
  id: string
  label: string
  icon: string
  accent: string
  blurb: string
  landing: string
  nav: ModuleNavSection[]
  /** Network capability flag required to show this module. Omit = always visible. */
  capability?: keyof Pick<Network, 'advertisingPlatform' | 'dataPlatform' | 'suiPlatform' | 'marketplacePlatform' | 'politicalApp'>
  /** User permission flag on the network required to show this module. Omit = no network permission needed. */
  permission?: keyof NetworkPermissionFlags
  /** Only shown to the super-admin accounts in `src/data/superAdmins.ts`, on any network. */
  superAdminOnly?: boolean
}

const STORAGE_KEY = 'adnuntius.workspace.module'

// Map route prefixes to module IDs
const ROUTE_MODULE_MAP: Array<[string, string]> = [
  ['/advertising', 'advertising'],
  ['/publishing', 'publishing'],
  ['/reports', 'reports'],
  ['/political', 'political'],
  ['/admin', 'admin'],
  ['/super-admin', 'super-admin'],
]

function resolveModuleFromRoute(pathname: string): string | null {
  for (const [prefix, moduleId] of ROUTE_MODULE_MAP) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) {
      return moduleId
    }
  }
  return null
}

function getStoredModule(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || 'political'
  } catch {
    return 'political'
  }
}

function setStoredModule(id: string) {
  try {
    localStorage.setItem(STORAGE_KEY, id)
  } catch {
    // ignore
  }
}

interface ModuleContextValue {
  currentModule: Module
  setModule: (id: string) => void
  modules: Module[]
}

const ModuleContext = createContext<ModuleContextValue | null>(null)

function filterModules(allModules: Module[], network: Network | null, user: ApiUser | null): Module[] {
  // Fall back to the session user so a deep link survives the profile load.
  const superAdmin = isSuperAdmin(user ? user.username : StorageService.getCurrentUser()?.username)
  const visible = allModules.filter((m) => !m.superAdminOnly || superAdmin)
  if (!network) return visible
  const supported = visible.filter((m) => !m.capability || network[m.capability])

  // Internal Adnuntius admins (incl. when masqueraded into) skip the permission
  // check — they get every module the network supports.
  if (!user || user.isAdnuntiusAdmin) return supported

  // Otherwise each module needs its own permission on this network. Being a
  // member of a network grants nothing by itself, so this can leave no modules.
  const perms = user.permissionsByNetwork[network.tagId]
  return supported.filter((m) => !m.permission || !!perms?.[m.permission])
}

export function ModuleProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { currentNetwork } = useNetwork()
  const user = useCurrentUser()
  const hasRetailProducts = useHasRetailProducts(!!user && !!currentNetwork?.advertisingPlatform)

  const allModules: Module[] = useMemo(
    () => [
      {
        id: 'advertising',
        label: t('nav.adServing'),
        icon: 'pi-send',
        accent: '#2f6df4',
        blurb: t('nav.adServingBlurb'),
        landing: '/advertising',
        capability: 'advertisingPlatform',
        permission: 'advertising',
        nav: [
          { items: [{ id: 'as-home', icon: 'pi-home', label: t('nav.home'), href: '/advertising' }] },
          {
            title: t('nav.advertising'),
            items: [
              { id: 'advertisers', icon: 'pi-id-card', label: t('nav.advertisers'), href: '/advertising/advertisers' },
              { id: 'orders', icon: 'pi-briefcase', label: t('nav.orders'), href: '/advertising/orders' },
              { id: 'line-items', icon: 'pi-bars', label: t('nav.lineItems'), href: '/advertising/lineitems' },
              { id: 'creatives', icon: 'pi-image', label: t('nav.creatives'), href: '/advertising/creatives' },
            ],
          },
          {
            title: t('nav.reports'),
            items: [
              { id: 'as-quick', icon: 'pi-chart-bar', label: t('nav.quickReports'), href: '/advertising/reports' },
              // Explore is Adnuntius-internal, as it is in the old UI.
              ...(isSuperAdmin(user?.username)
                ? [{ id: 'as-explore', icon: 'pi-compass', label: t('nav.explore'), href: '/advertising/explore' }]
                : []),

            ],
          },
        ],
      },
      {
        id: 'publishing',
        label: t('nav.publishing'),
        icon: 'pi-book',
        accent: '#0d9488',
        blurb: t('nav.publishingBlurb'),
        landing: '/publishing',
        capability: 'advertisingPlatform',
        permission: 'publishing',
        nav: [
          { items: [{ id: 'pub-home', icon: 'pi-home', label: t('nav.home'), href: '/publishing' }] },
          {
            title: t('nav.inventory'),
            items: [
              { id: 'earnings', icon: 'pi-building', label: t('nav.publishers'), href: '/publishing/earnings' },
              { id: 'sites', icon: 'pi-compass', label: t('nav.sites'), href: '/publishing/sites' },
              { id: 'ad-units', icon: 'pi-table', label: t('nav.adUnits'), href: '/publishing/ad-units' },
            ],
          },
          {
            title: t('nav.reports'),
            items: [
              { id: 'pub-quick', icon: 'pi-chart-bar', label: t('nav.quickReports'), href: '/publishing/reports' },
            ],
          },
        ],
      },
      {
        id: 'reports',
        label: t('nav.reports'),
        icon: 'pi-chart-line',
        accent: '#0891b2',
        blurb: t('nav.reportsBlurb'),
        landing: '/reports',
        capability: 'advertisingPlatform',
        permission: 'advertising',
        nav: [
          {
            title: t('nav.reports'),
            items: [
              { id: 'rep-reports', icon: 'pi-file', label: t('nav.reportHistory'), href: '/reports' },
              { id: 'rep-schedules', icon: 'pi-calendar', label: t('nav.reportSchedules'), href: '/reports/schedules' },
            ],
          },
          {
            title: t('nav.setup'),
            items: [
              { id: 'rep-templates', icon: 'pi-clone', label: t('nav.reportTemplates'), href: '/reports/templates' },
              { id: 'rep-translations', icon: 'pi-language', label: t('nav.reportTranslations'), href: '/reports/translations' },
            ],
          },
        ],
      },
      {
        id: 'political',
        label: t('nav.political'),
        icon: 'pi-flag',
        accent: '#6d28d9',
        blurb: t('nav.politicalBlurb'),
        landing: '/political',
        capability: 'politicalApp',
        permission: 'political',
        nav: [
          { items: [{ id: 'pol-home', icon: 'pi-home', label: t('nav.home'), href: '/political' }] },
          {
            title: t('nav.political'),
            items: [
              { id: 'elections', icon: 'pi-flag', label: t('nav.elections'), href: '/political/elections' },
              { id: 'political-entities', icon: 'pi-users', label: t('nav.politicalEntities'), href: '/political/entities' },
              { id: 'political-campaigns', icon: 'pi-megaphone', label: t('nav.politicalCampaigns'), href: '/political/campaigns' },
            ],
          },
          {
            title: t('nav.system'),
            items: [
              { id: 'pol-users', icon: 'pi-user', label: t('nav.users'), href: '/political/users' },
            ],
          },
        ],
      },
      {
        id: 'admin',
        label: t('nav.admin'),
        icon: 'pi-cog',
        accent: '#dc2626',
        blurb: t('nav.adminBlurb'),
        landing: '/admin',
        permission: 'manageSystem',
        nav: [
          { items: [{ id: 'admin-home', icon: 'pi-home', label: t('nav.home'), href: '/admin' }] },
          {
            title: t('nav.system'),
            items: [
              { id: 'admin-layouts', icon: 'pi-th-large', label: t('nav.layouts'), href: '/admin/layouts' },
              { id: 'admin-custom-events', icon: 'pi-zap', label: t('nav.customEvents'), href: '/admin/custom-events' },
              { id: 'admin-tiers', icon: 'pi-layers', label: t('nav.tiers'), href: '/admin/tiers' },
              { id: 'admin-data-exports', icon: 'pi-file-export', label: t('nav.dataExports'), href: '/admin/data-exports' },
              { id: 'admin-email-templates', icon: 'pi-envelope', label: t('nav.emailTemplates'), href: '/admin/email-templates' },
              { id: 'admin-email-translations', icon: 'pi-language', label: t('nav.emailTranslations'), href: '/admin/email-translations' },
              { id: 'admin-products', icon: 'pi-box', label: t('nav.products'), href: '/admin/products' },
              { id: 'admin-networks', icon: 'pi-globe', label: t('nav.networkSettings'), href: '/admin/networks' },
            ],
          },
          {
            title: t('nav.users'),
            items: [
              { id: 'admin-teams', icon: 'pi-users', label: t('nav.teamsNav'), href: '/admin/teams' },
              { id: 'admin-team-groups', icon: 'pi-sitemap', label: t('nav.teamGroups'), href: '/admin/team-groups' },
              { id: 'admin-users', icon: 'pi-user', label: t('nav.users'), href: '/admin/users' },
              { id: 'admin-roles', icon: 'pi-shield', label: t('nav.roles'), href: '/admin/roles' },
              { id: 'admin-notification-preferences', icon: 'pi-bell', label: t('nav.notificationPreferences'), href: '/admin/notification-preferences' },
            ],
          },
        ],
      },
      {
        id: 'super-admin',
        label: t('nav.superAdmin'),
        icon: 'pi-key',
        accent: '#b45309',
        blurb: t('nav.superAdminBlurb'),
        landing: '/super-admin',
        superAdminOnly: true,
        nav: [
          { items: [{ id: 'super-admin-home', icon: 'pi-home', label: t('nav.home'), href: '/super-admin' }] },
          {
            title: t('nav.system'),
            items: [
              { id: 'super-admin-release-notes', icon: 'pi-newspaper', label: t('nav.releaseNotes'), href: '/super-admin/release-notes' },
              { id: 'super-admin-diagnostics', icon: 'pi-wrench', label: t('nav.diagnostics'), href: '/super-admin/diagnostics' },
              { id: 'super-admin-line-item-comparison', icon: 'pi-th-large', label: t('nav.lineItemComparison'), href: '/super-admin/line-item-comparison' },
            ],
          },
        ],
      },
    ],
    [t, user]
  )

  const availableModules = useMemo(() => {
    const modules = filterModules(allModules, currentNetwork, user)
    const marketplace = !!currentNetwork?.marketplacePlatform
    // What the publishing module offers depends on who owns the inventory. A
    // marketplace network carries other publishers' sites, so it gets the
    // rules that govern them; elsewhere the network owns its sites and can
    // group them itself — the grouping a marketplace has no use for.
    return modules.map((m) => {
      // Retail products are only offered where a product feed has filled them.
      if (m.id === 'advertising') {
        if (!hasRetailProducts) return m
        return {
          ...m,
          nav: m.nav.map((section) =>
            section.title !== t('nav.advertising')
              ? section
              : {
                  ...section,
                  items: [
                    ...section.items,
                    { id: 'retail-products', icon: 'pi-box', label: t('nav.retailProducts'), href: '/advertising/retail-products' },
                  ],
                }
          ),
        }
      }
      if (m.id !== 'publishing') return m
      const nav = m.nav.map((section) =>
        marketplace || section.title !== t('nav.inventory')
          ? section
          : {
              ...section,
              items: [
                ...section.items,
                { id: 'site-groups', icon: 'pi-layers', label: t('nav.siteGroups'), href: '/publishing/site-groups' },
              ],
            }
      )
      return {
        ...m,
        nav: marketplace
          ? [
              ...nav,
              {
                title: t('nav.rules'),
                items: [
                  { id: 'blocklists', icon: 'pi-ban', label: t('nav.blocklists'), href: '/publishing/blocklists' },
                  { id: 'rulesets', icon: 'pi-sliders-h', label: t('nav.rulesets'), href: '/publishing/rulesets' },
                ],
              },
            ]
          : nav,
      }
    })
  }, [allModules, currentNetwork, user, hasRetailProducts, t])

  const [moduleId, setModuleId] = useState(() => {
    return resolveModuleFromRoute(location.pathname) || getStoredModule()
  })

  // Sync module when route changes
  useEffect(() => {
    const fromRoute = resolveModuleFromRoute(location.pathname)
    if (fromRoute && fromRoute !== moduleId) {
      setModuleId(fromRoute)
      setStoredModule(fromRoute)
    }
  }, [location.pathname])

  // Redirect if current module is not available for this network
  useEffect(() => {
    const fromRoute = resolveModuleFromRoute(location.pathname)
    if (fromRoute && !availableModules.some((m) => m.id === fromRoute)) {
      navigate('/select-module', { replace: true })
    }
  }, [location.pathname, availableModules, navigate])

  const currentModule = availableModules.find((m) => m.id === moduleId) || availableModules[0] || allModules[0]

  const setModule = useCallback(
    (id: string) => {
      const mod = availableModules.find((m) => m.id === id)
      if (!mod) return
      setModuleId(id)
      setStoredModule(id)
      navigate(mod.landing)
    },
    [navigate, availableModules]
  )

  return (
    <ModuleContext.Provider value={{ currentModule, setModule, modules: availableModules }}>
      {children}
    </ModuleContext.Provider>
  )
}

export function useModule() {
  const ctx = useContext(ModuleContext)
  if (!ctx) throw new Error('useModule must be used within ModuleProvider')
  return ctx
}
